/**
 * Web Push registration — registers /sw.js, requests permission,
 * subscribes via the browser's PushManager + VAPID, posts the
 * subscription to the backend, caches the device-token id for the
 * signout cleanup.
 *
 * Idempotent + cheap; safe to call on every page load. Skips silently
 * (returns null) when:
 *   - Service Workers aren't supported (older Safari etc.)
 *   - PushManager isn't supported (most desktop Safari < 16.4)
 *   - User declined permission
 *   - VAPID public key isn't configured (dev mode without env)
 *
 * Note on iOS Safari: web push only works for **installed PWAs** on
 * iOS, not regular tabs. We surface this elsewhere; this function
 * just returns null on those platforms.
 */
import { notificationsApi, APIError } from './api';

const STORAGE_KEY = 'wydm.push.device_token_id';
const SW_URL = '/sw.js';

const VAPID_PUBLIC_KEY = (import.meta as unknown as { env: { VITE_VAPID_PUBLIC_KEY?: string } })
  .env.VITE_VAPID_PUBLIC_KEY;

export async function registerForPushAsync(): Promise<{ deviceTokenId: string } | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.info('[push] skipped: no SW / PushManager support');
    return null;
  }
  if (!VAPID_PUBLIC_KEY) {
    console.info('[push] skipped: VITE_VAPID_PUBLIC_KEY not set');
    return null;
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.register(SW_URL);
  } catch (err) {
    console.warn('[push] SW register failed', err);
    return null;
  }
  // Wait for the SW to be ready (active controller available).
  await navigator.serviceWorker.ready;

  // Permission. Note: Notification.requestPermission can only be called
  // from a user gesture on iOS; on desktop it works from any context.
  // For M4.A we call this at sign-in time / from a button; the browser
  // handles the gesture requirement itself.
  if (Notification.permission === 'default') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') {
      console.info('[push] skipped: permission not granted');
      return null;
    }
  } else if (Notification.permission !== 'granted') {
    console.info('[push] skipped: permission previously denied');
    return null;
  }

  // Subscribe (or re-use existing).
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    } catch (err) {
      console.warn('[push] PushManager.subscribe failed', err);
      return null;
    }
  }

  const json = subscription.toJSON();
  const endpoint = json.endpoint;
  const p256dh = (json.keys && json.keys.p256dh) || '';
  const auth = (json.keys && json.keys.auth) || '';

  if (!endpoint || !p256dh || !auth) {
    console.warn('[push] subscription missing fields');
    return null;
  }

  // The backend's device_tokens.token is unique per (user, token); we
  // use a SHA-256 of the endpoint so re-subscribes from the same
  // browser collapse onto the same row. SubtleCrypto is universally
  // available where PushManager is, so no fallback needed.
  const tokenHash = await sha256Hex(endpoint);

  try {
    const reg = await notificationsApi.registerToken({
      platform: 'web',
      token: tokenHash,
      web_endpoint: endpoint,
      web_p256dh: p256dh,
      web_auth: auth,
      device_name: navigator.userAgent.slice(0, 80),
    });
    localStorage.setItem(STORAGE_KEY, reg.id);
    console.info('[push] registered', reg.id);
    return { deviceTokenId: reg.id };
  } catch (err) {
    if (err instanceof APIError) {
      console.warn('[push] backend register failed', err.code, err.message);
    } else {
      console.warn('[push] backend register failed', err);
    }
    return null;
  }
}

/**
 * Tell the backend to forget this browser's push subscription.
 * Call before clearing the auth session on signout.
 *
 * Also unsubscribes the PushManager subscription locally so the
 * browser stops trying to deliver pushes to it.
 */
export async function unregisterPushAsync(): Promise<void> {
  const id = localStorage.getItem(STORAGE_KEY);
  if (id) {
    try {
      await notificationsApi.unregisterToken(id);
    } catch (err) {
      console.warn('[push] backend unregister failed', err);
    } finally {
      localStorage.removeItem(STORAGE_KEY);
    }
  }
  // Best-effort local unsubscribe (won't error if SW isn't registered).
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      await sub?.unsubscribe();
    } catch {
      /* ignore */
    }
  }
}

// urlBase64ToUint8Array converts a VAPID public key (urlsafe base64,
// no padding) into the Uint8Array PushManager.subscribe expects.
// Standard utility from the Web Push spec docs.
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function sha256Hex(s: string): Promise<string> {
  const enc = new TextEncoder().encode(s);
  const hash = await crypto.subtle.digest('SHA-256', enc);
  return [...new Uint8Array(hash)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
